import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort, MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import {
  merge,
  Subject,
  debounceTime,
  distinctUntilChanged,
  first,
  startWith,
  takeUntil,
} from 'rxjs';
import { AxiosError } from 'axios';
import { CreateCaseDialogComponent } from 'src/app/components/dashboard/clients-list/create-case-dialog/create-case-dialog.component';
import { EditClientDialogComponent } from 'src/app/components/dashboard/clients-list/edit-client-dialog/edit-client-dialog.component';
import { EditCaseDialogComponent } from 'src/app/helper/edit-case-dialog/edit-case-dialog.component';
import {
  ClientCreditTransferItem,
  ClientDataTableDetailsResponse,
  ClientDataTableItem,
  ClientsService,
} from 'src/app/services/clients/clients.service';
import { CreditsService } from 'src/app/services/credits/credits.service';
import { CaseTableItem } from 'src/app/shared/payee-cases/types';
import { AddCreditsDialogComponent } from './add-credits-dialog/add-credits-dialog.component';
import { ConfirmMergeDialogComponent } from './confirm-merge-dialog/confirm-merge-dialog.component';
import { CreateClientDialogComponent } from './create-client-dialog/create-client-dialog.component';
import { DeleteClientDialogComponent } from './delete-client-dialog/delete-client-dialog.component';
import { ClientReferenceIdFilterComponent } from './client-reference-id-filter/client-reference-id-filter.component';
import { ClientNameFilterComponent } from './client-name-filter/client-name-filter.component';
import { ClientTaxNumberFilterComponent } from './client-tax-number-filter/client-tax-number-filter.component';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeIconButtonComponent } from '../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { CurrencyPipe } from '@angular/common';

type SortBy = 'name' | 'tax_number' | 'open_cases' | 'closed_cases';

@Component({
  selector: 'app-clients-list',
  templateUrl: './clients-list.component.html',
  styleUrls: ['./clients-list.component.scss'],
  imports: [
    ClientReferenceIdFilterComponent,
    ClientNameFilterComponent,
    ClientTaxNumberFilterComponent,
    PayeeButtonComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatCheckbox,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    CurrencyPipe,
  ],
})
export class ClientsListComponent implements OnInit, OnDestroy {
  private clientsService = inject(ClientsService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);
  private creditsService = inject(CreditsService);

  private readonly destroy = new Subject<void>();

  readonly clientName = new FormControl<string | null>(null);
  readonly clientReferenceId = new FormControl<string | null>(null);
  readonly clientTaxNumber = new FormControl<string | null>(null);

  sort_by?: SortBy;
  sort_direction: 'asc' | 'desc' = 'asc';

  tableLoading = false;
  updateLoading = false;

  readonly dataSource = new MatTableDataSource<ClientDataTableItem>();
  readonly displayedColumns: string[] = [
    'client_reference_id',
    'name',
    'tax_number',
    'open_cases',
    'closed_cases',
    'auto_start_hard_2',
    'uses_credits',
    'credits',
    'actions',
  ];

  // pagination
  length: number = 0;
  pageSize = 10;
  pageIndex = 0;

  expandedElement: ClientDataTableItem | null = null;
  expandedElementData: ClientDataTableDetailsResponse | null = null;

  // Merge functionality
  selectedClientForMerge: ClientDataTableItem | null = null;

  ngOnInit(): void {
    if (this.route.snapshot.queryParams.filter) {
      this.clientName.patchValue(this.route.snapshot.queryParams.filter);
    }

    if (this.route.snapshot.queryParams.client_reference_id) {
      this.clientReferenceId.patchValue(
        this.route.snapshot.queryParams.client_reference_id,
      );
    }

    if (this.route.snapshot.queryParams.tax_number) {
      this.clientTaxNumber.patchValue(this.route.snapshot.queryParams.tax_number);
    }

    merge(
      this.clientName.valueChanges,
      this.clientReferenceId.valueChanges,
      this.clientTaxNumber.valueChanges,
    )
      .pipe(
        takeUntil(this.destroy),
        debounceTime(500),
        distinctUntilChanged(),
        startWith(null),
      )
      .subscribe({
        next: async () => {
          this.pageIndex = 0;
          this.router.navigate([], {
            queryParams: {
              filter: this.clientName.value,
              client_reference_id: this.clientReferenceId.value,
              tax_number: this.clientTaxNumber.value,
            },
          });
          await this.setClients();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
  }

  navigateToCases(client: ClientDataTableItem) {
    this.router.navigate(['/admin/cases-list'], {
      queryParams: {
        client_id: client.id,
      },
    });
  }

  async copyIdToClipboard(
    client: ClientDataTableItem,
    event: Event,
  ): Promise<void> {
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(client.client_reference_id);
    } catch (error) {
      console.error('Failed to copy to clipboard', error);
    }
  }

  createCase(client: ClientDataTableItem): void {
    const createRef = this.dialog.open(CreateCaseDialogComponent, {
      data: client,
      autoFocus: false,
    });
    createRef
      .afterClosed()
      .pipe(first())
      .subscribe((caseItem?: CaseTableItem) => {
        if (!caseItem) {
          return;
        }
        const editRef = this.dialog.open(EditCaseDialogComponent, {
          data: caseItem,
          autoFocus: false,
        });
        editRef
          .afterClosed()
          .pipe(first())
          .subscribe(async () => {
            await this.setClients();
          });
      });
  }

  async setClients() {
    try {
      this.tableLoading = true;
      const res = await this.clientsService.getClientsDataTable({
        client_reference_id: this.clientReferenceId.value || undefined,
        name: this.clientName.value || undefined,
        tax_number: this.clientTaxNumber.value || undefined,
        page: this.pageIndex + 1,
        per_page: this.pageSize,
        sort_by: this.sort_by,
        sort_direction: this.sort_direction,
      });

      this.length = res.pagination.total;
      this.dataSource.data = res.data.filter(
        (client) =>
          !this.selectedClientForMerge ||
          client.id !== this.selectedClientForMerge!.id,
      );
    } catch (error) {
      console.log('Error while loading clients', error);
    } finally {
      this.tableLoading = false;
    }
  }

  get hasActiveFilters(): boolean {
    return !!this.clientName.value || !!this.clientTaxNumber.value;
  }

  clearAllFilters(): void {
    this.clientName.setValue('');
    this.clientTaxNumber.setValue('');
    this.setClients();
  }

  onNameFilterChange(value: string | null): void {
    this.clientName.setValue(value);
    // Trigger the existing filter logic
    this.setClients();
  }

  async pageChange(event: PageEvent) {
    if (event.pageSize !== this.pageSize) {
      this.pageSize = event.pageSize;
    }
    if (event.pageIndex !== this.pageIndex) {
      this.pageIndex = event.pageIndex;
    }
    await this.setClients();
  }

  async sortData(event: Sort): Promise<void> {
    this.sort_by = event.active as SortBy;
    this.sort_direction = event.direction || 'asc';

    await this.setClients();
  }

  async updateExpandedElement(element: ClientDataTableItem): Promise<void> {
    if (this.expandedElement === element) {
      this.expandedElement = null;
      this.expandedElementData = null;
    } else {
      this.expandedElement = element;
      this.expandedElementData =
        await this.clientsService.getClientsDataTableDetails(element.id);
    }
  }

  editClient(event: Event, element: ClientDataTableItem) {
    event.stopPropagation();
    this.dialog.open(EditClientDialogComponent, {
      data: element,
      autoFocus: false,
      maxHeight: '90%',
    });
  }

  deleteClient(event: Event, element: ClientDataTableItem) {
    event.stopPropagation();
    const ref = this.dialog.open(DeleteClientDialogComponent, {
      data: element,
      autoFocus: false,
      maxHeight: '90%',
    });
    ref
      .afterClosed()
      .pipe(first())
      .subscribe({
        next: async (deleted?: boolean) => {
          if (deleted) {
            await this.setClients();
          }
        },
      });
  }

  createClient(): void {
    const ref = this.dialog.open<
      CreateClientDialogComponent,
      null,
      ClientDataTableItem | undefined
    >(CreateClientDialogComponent, {
      autoFocus: false,
      maxHeight: '90%',
    });
    ref
      .afterClosed()
      .pipe(first())
      .subscribe({
        next: async (client) => {
          if (client) {
            this.clientName.patchValue(client.name, { emitEvent: false });
            this.dataSource.data = [client];
          }
        },
      });
  }

  getIsDeletableMessage(element: ClientDataTableItem): string {
    if (element.is_deletable === true) {
      return '';
    }
    if (element.is_deletable === 'client-has-users') {
      return 'A hitelezőt nem lehet törölni, mert van hozzárendelve felhasználó.';
    }
    if (element.is_deletable === 'has-started-cases') {
      return 'A hitelezőt nem lehet törölni, mert már indított ügyet.';
    }

    return 'Az ügyet nem lehet törölni.';
  }

  async cancelMerge(): Promise<void> {
    this.selectedClientForMerge = null;
    await this.setClients();
  }

  async selectClientForMerge(client: ClientDataTableItem): Promise<void> {
    if (!this.selectedClientForMerge) {
      this.selectedClientForMerge = client;
      this.dataSource.data = this.dataSource.data.filter(
        (c) => c.id !== client.id,
      );
      return;
    }

    const dialogRef = this.dialog.open(ConfirmMergeDialogComponent, {
      data: {
        masterClient: this.selectedClientForMerge,
        clientToMerge: client,
      },
      autoFocus: false,
    });

    dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe(async (result) => {
        if (result && this.selectedClientForMerge) {
          try {
            await this.clientsService.mergeClients({
              master_client_id: this.selectedClientForMerge.id,
              client_to_merge_id: client.id,
            });
            this.snackBar.open('Hitelezők összevonása sikeres', 'OK', {
              duration: 3000,
            });
            await this.setClients();
          } catch (error: unknown) {
            let errorMessage = 'Hitelezők összevonása sikertelen';

            if (error instanceof AxiosError) {
              if (error.response?.data?.error === 'DUPLICATE_PARTNER') {
                errorMessage =
                  'Nem lehet összevonni a hitelezőket - duplikált partner kapcsolatok';
              } else if (
                error.response?.data?.error === 'DUPLICATE_CLIENT_USER'
              ) {
                errorMessage =
                  'Nem lehet összevonni a hitelezőket - duplikált felhasználói kapcsolatok';
              }
            }

            this.snackBar.open(errorMessage, 'OK', {
              duration: 5000,
            });
            console.error('Error while merging clients', error);
          }
        }
      });
  }

  async toggleAutoStartHard2(
    client: ClientDataTableItem,
    event: MatCheckboxChange,
  ): Promise<void> {
    try {
      this.updateLoading = true;
      client.auto_start_hard_2 = event.checked;

      await this.creditsService.toggleAutoStartHard2(client.id, event.checked);
    } catch (error: AxiosError | unknown) {
      client.auto_start_hard_2 = !event.checked;

      let message = 'Hiba mentés közben';
      if (error instanceof AxiosError && error.response?.data?.message) {
        message = error.response.data.message;
      }

      console.error('Error while toggling auto start hard 2', error);
      this.snackBar.open(message, 'OK', { duration: 5000 });
    } finally {
      this.updateLoading = false;
    }
  }

  async toggleUsesCredits(
    client: ClientDataTableItem,
    event: MatCheckboxChange,
  ): Promise<void> {
    try {
      this.updateLoading = true;

      if (!event.checked) {
        await this.toggleAutoStartHard2(client, event);
      }

      client.uses_credits = event.checked;

      await this.creditsService.toggle(client.id, event.checked);
    } catch (error: AxiosError | unknown) {
      client.uses_credits = !event.checked;

      let message = 'Hiba mentés közben';
      if (error instanceof AxiosError && error.response?.data?.message) {
        message = error.response.data.message;
      }

      console.error('Error while toggling credits', error);
      this.snackBar.open(message, 'OK', { duration: 5000 });
    } finally {
      this.updateLoading = false;
    }
  }

  addCredit(client: ClientDataTableItem): void {
    const ref = this.dialog.open<
      AddCreditsDialogComponent,
      ClientDataTableItem,
      ClientDataTableItem
    >(AddCreditsDialogComponent, {
      data: client,
    });

    ref
      .afterClosed()
      .pipe(first())
      .subscribe((r) => {
        if (r) {
          Object.assign(client, r);
        }
      });
  }

  async payCreditTransfer(line: ClientCreditTransferItem): Promise<void> {
    if (!this.expandedElement || !this.expandedElementData) {
      return;
    }

    try {
      await this.creditsService.pay(this.expandedElement.id, line.id);
      line.state = 'paid';

      const i = this.expandedElement.credits.findIndex(
        (c) => c.currency_iso === line.currency_iso,
      );
      if (i >= 0) {
        this.expandedElement.credits[i].remaining += line.amount;
      } else {
        this.expandedElement.credits.push({
          currency_iso: line.currency_iso,
          remaining: line.amount,
          used: 0,
        });
      }
    } catch (error) {
      console.error('Error while paying credit transfer', error);
    }
  }
}
